import { isTrackedClass } from '@kb3d/models/interfaces/tracked-class';
import { FontService } from '@kb3d/models/services/font-service';
import { HttpService, IHttpEnvironment } from '@kb3d/models/services/http-service';
import { MaterialService } from '@kb3d/models/services/material-service';
import { MeshService } from '@kb3d/models/services/mesh-service';
import { getConstructorParamsForModel } from './decorators/inject-decorator';
import { IDecoratedModel } from './decorators/kb3d-decorator';
import { Constructor, IKb3dModel, Kb3dModels, instanceOf, instanceOfAny } from './decorators/model-utils';
import { defaults } from './default-values';
import {
    HandlerMap,
    SetMap,
    TagArrayMap,
} from './handlers';
import { IDraggable, Kb3dBoundingBoxChangeHandler, Kb3dDragHandler, Kb3dPointerHandler } from './client-enums';
import { IKb3dObject, ISceneInfo } from './kb3d-models';
import { Kb3dObject } from './kb3d-object';
import { LightNode, isLightNode } from './lights/light-node';
import { MaterialNode, isMaterialNode } from './materials/material-node';
import { MeshGroupNode } from './meshes/mesh-group-node';
import { isMeshNode } from './meshes/mesh-utilities';
import { SpaceNode } from './meshes/space-node';
import { SceneNode } from './scene-node';
import { Token } from './token';
import { isGuid, isObject } from './utilities';

const SET_TYPE = '__set';

/** contains all kb3dobjects keyed by their dynamic Id, to fetch nodes from the 3d engine ID */
const dynamicDb: Map<string, Kb3dObject> = new Map<string, Kb3dObject>();

export function getByDynamicId<T extends Kb3dObject>(dynamicId: string, throwOnNotFound: true): T;
export function getByDynamicId<T extends Kb3dObject>(dynamicId: string, throwOnNotFound?: boolean): T | undefined;
export function getByDynamicId<T extends Kb3dObject>(dynamicId: string, throwOnNotFound = false): T | undefined {
    if (throwOnNotFound && !dynamicDb.has(dynamicId)) {
        throw `kb3d object with id ${dynamicId} not found`;
    }
    return dynamicDb.get(dynamicId) as T | undefined;
}

export interface ISerializeOptions {
    /** whether to include children in the serialization.  Defaults to true */
    includeChildNodes?: boolean;
    /** whether to omit properties that are the same as the default value of that property.  Defaults to true */
    omitDefaultValues?: boolean;
    /** internal use */
    isRoot?: boolean;
}

export interface IJsonSerializeOptions extends ISerializeOptions {
    prettyPrint?: boolean;
}

export interface IDeserializeOptions {
    /** whether to create children while deserializing.  Defaults to true */
    includeChildNodes?: boolean;
    /** when deserializing, set to true to issue new id's to all the kb3dobjects.  Defaults to false  */
    issueNewIds?: boolean;
    /** when copying properties from one node to another, we don't want to copy certain properties */
    copyingToDifferentNode?: boolean;
}

export enum ImportOperation {
    ignore,
    add,
    replace,
}

export interface IImportPreview {
    type: Token;
    node: Partial<IKb3dObject>;
    namePath: string;
    matchingNode: Kb3dObject;
    operation: ImportOperation;
    children: IImportPreview[];
    parent?: IImportPreview;
}

export interface IKb3dManagerOptions {
    services?: Map<Token, any>;
    /** the root domain that holds assets like meshes and textures. Leave empty to call for them on the host server */
    assetRoot?: string;
    forceFullSizeTextures?: boolean;
    environment?: IHttpEnvironment;
}

export class Kb3dManager {
    services: Map<Token, any>;
    isDevEnvironment = true;

    constructor(options?: IKb3dManagerOptions) {
        //if no services are provided, then use the default services
        if (options && options.services) {
            this.services = options.services;
        } else {
            const httpService = new HttpService(options?.environment);
            if (options?.environment && options.environment.lastDeploy) {
                this.isDevEnvironment = false;
            }
            this.services = new Map<Token, any>([
                [Token.HttpService, httpService],
                [
                    Token.MaterialService,
                    new MaterialService(httpService, options?.assetRoot, options?.forceFullSizeTextures),
                ],
                [Token.MeshService, new MeshService(httpService, options?.assetRoot)],
                [Token.FontService, new FontService(httpService, options?.assetRoot)],
            ]);
        }
    }

    /** contains all kb3dobjects */
    private _db = new Map<string, Kb3dObject>();
    /** contains all objects keyed by their token */
    private _tokenDb = new SetMap<Token, Kb3dObject>();
    /** contains all nodes (but not other kinds of objects) */
    private _nodeDb = new Map<string, Kb3dObject>();
    /** holds a cache of name searches that have been performed with their results */
    private _nameSearchCache = new Map<string, Kb3dObject[]>();
    /** contains nested scenes keyed by their nestedId */
    private _nestedSceneDb = new Map<string, SceneNode>();

    /** holds a cache of click handlers that have been added to nodes in the context of this manager */
    public readonly clickHandlers = new HandlerMap<Kb3dPointerHandler>(this);
    public readonly doubleClickHandlers = new HandlerMap<Kb3dPointerHandler>(this);
    public readonly dragHandlers = new HandlerMap<Kb3dDragHandler>(this);
    public readonly draggables = new HandlerMap<IDraggable>(this);
    public readonly hotspotClickHandlers = new TagArrayMap<any, (args: any) => void>();
    public readonly boundingboxChangeHandlers = new HandlerMap<Kb3dBoundingBoxChangeHandler>(this);
    public readonly cameraChangeHandlers: Array<{ tag: string; orbit: boolean; pan: boolean; zoom: boolean; handler: (args: any) => void }> = [];

    public getService<TService>(serviceToken: Token<TService>): TService {
        return this.services.get(serviceToken);
    }

    public dispose() {
        /**only clear items out of the dynamic db that were part of this manager (as it is static and holds dynamic ids for all managers) */
        for (const [, value] of this._db) {
            dynamicDb.delete(value._dynamicId);
        }
        this._db.clear();
        this._nodeDb.clear();
        this.clickHandlers.clear();
        this.doubleClickHandlers.clear();
        this.draggables.clear();
        this.hotspotClickHandlers.clear();
        this.boundingboxChangeHandlers.clear();
        this.cameraChangeHandlers.length = 0;
        this._nameSearchCache.clear();
        this._tokenDb.clear();
        this._nestedSceneDb.clear();
    }

    /**
     * Accepts either a node id or a name to search for nodes.  If using
     * node name wildcards * are allowed.  The action provided will be run
     * on each of the matching nodes, with the args passed in to that action.
     * @param idOrName
     * @param action
     * @param args
     * @param useCache
     */
    public runOnNodes<T extends Kb3dObject, TArgs>(
        idOrName: string,
        action: (node: T, args?: TArgs) => void,
        args?: TArgs,
        useCache = true
    ) {
        const node = this.getById<T>(idOrName);
        if (node){ 
            action(node, args);
        } else {
            const nodes = this.getNodesByName(idOrName, useCache);
            for (const node of nodes) {
                action(node as T, args);
            }
        }
    }
    // @child1/xxxxx-xxxxxx-xxxx
    public getById<T extends Kb3dObject>(id: string): T | undefined {
        if (id && id.startsWith('@')) {
            //it's a nested path id
            const segments = id.split('/');
            const nestedId = segments.shift()!.substring(1).toLowerCase();
            const nestedScene = this._nestedSceneDb.get(nestedId);
            if (nestedScene) {
                let subId = segments.join('/');
                if (segments.length > 1) subId = '@' + subId;
                return nestedScene._manager!.getNodeByIdOrName<T>(subId);
            }
        } else {
            return this._db.get(id) as T | undefined;
        }
    }

    public getAll() {
        return [...this._db.values()];
    }

    public getAllNodes() {
        return [...this._nodeDb.values()];
    }

    /**
     * Accepts either a node id or a name to search for nodes.  Only returns the first
     * matching node. If using node name wildcards * are allowed.
     * @param idOrName
     * @param useCache
     */
    public getNodeByIdOrName<T extends Kb3dObject>(idOrName: string | T, useCache = true): T | undefined {
        if (typeof idOrName == 'string') {
            if (idOrName.startsWith('@')) {
                return this.getById<T>(idOrName);
            } else {
                const result = this.getById<T>(idOrName);
                if (result) {
                    return result;
                } else {
                    return this.getFirstNodeByName(idOrName, useCache) as any as T;
                }
            }
        } else if (idOrName instanceof Kb3dObject) {
            return idOrName;
        }
    }

    /**
     * Accepts either a node id or a name to search for nodes.  If using
     * node name wildcards * are allowed.
     * @param idOrName
     * @param useCache
     */
    public getNodesByIdOrName<T extends Kb3dObject>(idOrName: string | T, useCache = true): T[] {
        const res = [];

        if (typeof idOrName == 'string') {
            const n = this.getById<T>(idOrName);
            if (n) {
                res.push(n);
            } else {
                res.push(...this.getNodesByName(idOrName, useCache));
            }
        } else if (idOrName instanceof Kb3dObject) {
            res.push(idOrName);
        }
        return res as T[];
    }

    /**
     * Get's the first node that matches the name given.  Wildcards * are allowed.
     * @param name
     * @param useCache
     */
    public getFirstNodeByName(name: string, useCache = true) {
        const nodes = this.getNodesByName(name, useCache);
        if (nodes && nodes.length) {
            return nodes[0];
        }
    }

    /**
     * Returns a list of nodes that match the name given.
     * Wildcards * are allowed.
     * Results will be fed from a search cache if useCache is true
     * @param name
     * @param useCache
     */
    public getNodesByName<T extends Kb3dObject>(name: string, useCache = true): T[] {
        if (useCache && this._nameSearchCache.has(name)) {
            return this._nameSearchCache.get(name) as T[];
        }

        const results = new Array<T>();
        const re = this.getWildcardRegex(name);

        for (const [, node] of this._db) {
            if (re.test(node.name)) {
                results.push(node as T);
            }
        }

        this._nameSearchCache.set(name, results);

        return results;
    }

    public getNodesContainingName<T extends Kb3dObject>(name: string, useCache = true): T[] {
        if (useCache && this._nameSearchCache.has(name)) {
            return this._nameSearchCache.get(name) as T[];
        }

        const results = new Array<T>();
        const re = this.getWildcardRegex(name);

        for (const [, node] of this._db) {
            if (re.test(node.name)) {
                results.push(node as T);
            }
        }

        this._nameSearchCache.set(name, results);

        return results;
    }

    public getByToken<T extends Kb3dObject>(token: Token<T>) {
        return [...this._tokenDb.get(token).values()] as T[];
    }

    public getByTokens<T extends Kb3dObject = Kb3dObject>(...tokens: Token[]) {
        return tokens.flatMap(t => [...this._tokenDb.get(t).values()]) as T[];
    }

    /** escapes the given string so that any special characters will be processed literally in a regex search */
    protected escapeRegex(str: string) {
        return str.replace(/([.*+?^=!:${}()|[\]/\\])/g, '\\$1');
    }

    /**
     * Given a string with wildcards *, will provide a regex that can be used to
     * match strings.
     * @param rule
     */
    protected getWildcardRegex(rule: string): RegExp {
        // "."  => Find a single character, except newline or line terminator
        // ".*" => Matches any string that contains zero or more characters
        rule = rule.split('*').map(this.escapeRegex).join('.*');

        // "^"  => Matches any string with the following at the beginning of it
        // "$"  => Matches any string with that in front at the end of it
        rule = '^' + rule + '$';

        //Create a regular expression object for matching string
        return new RegExp(rule, 'i');
    }

    /**
     * absorbs a kb3d object that already exists into the manager.
     * @param o
     */
    public register<T>(o: T) {
        if (o instanceof Kb3dObject) {
            if (!this._db.has(o.id)) {
                if (isTrackedClass(o)) {
                    o.isModified = true; //force a full round of change detection if this is a newly registered object
                }
            }

            this._db.set(o.id, o);
            dynamicDb.set(o._dynamicId, o);
            this._tokenDb.add(Token.withKey(o.$type), o);

            if (o.isNode()) {
                this._nodeDb.set(o.id, o);
            }
        }
    }

    public unregister<T>(o: T) {
        if (o instanceof Kb3dObject) {
            this.clickHandlers.deleteByKey(o as any as SpaceNode);
            this.doubleClickHandlers.deleteByKey(o as any as SpaceNode);
            this.draggables.deleteByKey(o as any as SpaceNode);
            this.boundingboxChangeHandlers.deleteByKey(o as any as SpaceNode);
            this._db.delete(o.id);
            dynamicDb.delete(o._dynamicId);
            this._tokenDb.removeValue(Token.withKey(o.$type), o);

            if (o.isNode()) {
                this._nodeDb.delete(o.id);
            }

            if (o instanceof SceneNode && o.isNested()) {
                this._nestedSceneDb.delete(o.nestedId);
            }
        }
    }

    /**
     * Creates an instance of a kb3d model, given a token
     * and an anonymous object in the shape of the model that
     * can be used to set properties on it.
     *
     * As part of the creation process, we do things in this order:
     *
     * 1. Create an instance of the class.  If a create function was provided
     *    in the decorator for the class, that is used to create the instance.
     *    If not, then it's created by calling the constructor of the class
     *    with no arguments.
     * 2. We then apply default values to the properties of the class
     * 3. We then apply the args that were passed in to set properties on the class
     * 4. We then register the object into the map using it's id so it can be quickly retrieved
     */
    public create<T>(token: Token<T>, args: Partial<T> = {}, options?: IDeserializeOptions): T {
        options = { includeChildNodes: true, ...options };

        const modelDef = Kb3dModels.get(token);
        if (!modelDef) {
            throw `could not find model with token: ${token.key}`;
        }
        const constructorParameters = getConstructorParamsForModel(modelDef);
        const hydratedParameters: any[] = [];
        for (const i of constructorParameters) {
            if (!i.ignore) {
                const service = this.services?.get(i.token);
                if (!service) {
                    throw `Injected service of type '${i.token.key} could not be found to inject into the constructor of type '${token.key}`;
                }
                hydratedParameters.push(service);
            }
        }

        let model: T;
        if (modelDef.create) {
            model = modelDef.create(args);
        } else {
            model = new (modelDef.classConstructor as Constructor<any>)(...hydratedParameters);
        }

        //now initialize the model for when no create function was given
        if (!modelDef.create) {
            this.initializeModel(isTrackedClass(model) ? model.rawNode : model, args, modelDef, options);
        }

        if (model instanceof Kb3dObject) {
            model._manager = this;
        }
        if (model instanceof SceneNode) {
            model.calculateParents(); //because calculateParents also registers the item, we must call this after initialization, otherwise it won't have the right id
        }

        if (modelDef.afterCreate) {
            modelDef.afterCreate(model);
        }

        //need to clear the name cache since we just created a new node
        //no need to clear if the node is a primitive as they are never fetched by name
        if(!token.isPrimitive()) {
            this._nameSearchCache.clear();
        }

        return model;
    }

    public serializeToJson<T extends IDecoratedModel>(instance: T, options?: IJsonSerializeOptions): string {
        const o = this.serialize(instance, options);
        const json = JSON.stringify(o, null, options?.prettyPrint ? 2 : undefined);
        return json;
    }

    public serialize<T extends IDecoratedModel>(instance: T, options?: ISerializeOptions): Partial<T> | undefined {
        options = {
            includeChildNodes: true,
            isRoot: true,
            omitDefaultValues: true,
            ...options,
        };

        let result: any = { $type: instance.$type };
        const defaultValues = (defaults as any)[instance.$type];

        for (const key in instance) {
            if (
                // Never persist properties beginning with an underscore
                key.substr(0, 1) !== '_' &&
                // If a property is the same as a default value, remove it
                (!options.omitDefaultValues ||
                    !defaultValues ||
                    // We need to make sure that faceLoops always gets serialized, even if it's an empty array
                    (instance.$type === Token.ChamferFeature.key && key === 'faceLoops') ||
                    !this.shallowEquals(defaultValues[key], (instance as any)[key]))
            ) {
                if (!isObject(instance[key])) {
                    // For primitives, just copy the values over
                    result[key] = instance[key];
                } else if (Array.isArray(instance[key])) {
                    const sourceArray = instance[key] as T[];
                    if (sourceArray.length && typeof sourceArray[0] !== 'object') {
                        //it's an array of primitives
                        result[key] = sourceArray.slice(0); //clone the array of primitives
                    } else {
                        result[key] = []; //make sure there is an array on target prop.  Recreate it if one is there already.
                        for (const sourceItem of sourceArray) {
                            if (
                                !sourceItem.$type ||
                                !Token.withKey(sourceItem.$type).isNode() ||
                                options.includeChildNodes
                            ) {
                                // exclude it if it's a kb3d model and includeChildren is false
                                const targetItem = this.serialize(sourceItem, { ...options, isRoot: false });
                                if (targetItem) {
                                    result[key].push(targetItem);
                                }
                            }
                        }
                    }
                } else if ((instance[key] as any) instanceof Map) {
                    console.error('implement serializing maps');
                } else if ((instance[key] as any) instanceof Set) {
                    result[key] = {
                        $type: SET_TYPE,
                        values: Array.from((instance[key] as Set<any>).values()),
                    };
                } else {
                    // For anything else, try deserializing. The deserializer will just copy the property if it is not a serialized class.
                    result[key] = this.serialize(instance[key], { ...options, isRoot: false });
                }
            }
        }

        if (instance.$type) {
            const modelDef = Kb3dModels.get(Token.withKey(instance.$type));
            if (modelDef && modelDef.onSerializing) {
                const include = modelDef.onSerializing(instance, result);
                // Always serialize the object if it is the root object. Otherwise, let the class decide.
                if (include === false && !options.isRoot) {
                    result = undefined;
                }
            }
        }
        return result;
    }

    /**
     * See notes for the create() function as this is virtually the same.
     * The only difference is that the type here is discovered through the $type
     * property of the args object instead of an explicit token.
     * @param object
     */
    public deserialize<T = any>(object: any, options?: IDeserializeOptions): T {
        if (!object || !object.$type) {
            return object;
        } else if (object.$type === SET_TYPE) {
            return new Set<any>(object.values) as any;
        }
        return this.create(Token.withKey(object.$type), object, options);
    }

    public deserializeJson<T = any>(json: string): T {
        const object = JSON.parse(json);
        return this.deserialize<T>(object);
    }

    public clone<T extends IDecoratedModel>(objectToClone: T): T {
        //make sure it's a kb3d model
        if (!objectToClone) throw 'no object provided to clone';
        if (!('$type' in objectToClone)) {
            throw 'object passed to clone function must be a kb3d model and have a $type defined';
        }
        const json = this.serializeToJson(objectToClone);
        const object = JSON.parse(json) as Kb3dObject;

        return this.deserialize<T>(object, { issueNewIds: true });
    }

    public nestScene(nestedId: string, parent: SceneNode, serializedNested: Partial<SceneNode> = {}) {
        const nestedMan = new Kb3dManager({ services: this.services });
        if (serializedNested.lights) {
            serializedNested.lights.clear();
        }
        const nested = nestedMan.create(Token.SceneNode, serializedNested);
        nested.nestedId = nestedId.replace('/', '');
        parent.scenes.push(nested);
        this._nestedSceneDb.set(nested.nestedId.toLowerCase(), nested);

        return nested;
    }

    protected getNodeNamePathMap() {
        const meshMap = new Map<string, Kb3dObject>();
        const matMap = new Map<string, MaterialNode>();
        const lightMap = new Map<string, LightNode>();
        const allNodes = this._nodeDb.values();
        for (const n of allNodes) {
            if (isLightNode(n)) {
                lightMap.set(n.getNamePath(), n);
            } else if (isMaterialNode(n)) {
                matMap.set(n.getNamePath(), n);
            } else {
                meshMap.set(n.getNamePath(), n);
            }
        }
        return {
            meshes: meshMap,
            lights: lightMap,
            materials: matMap,
        };
    }

    public previewImport(parent: SceneNode | MeshGroupNode, imported: ISceneInfo) {
        const sceneNode = parent._parentScene;
        if (!sceneNode || !imported.scene) return;

        const nMap = this.getNodeNamePathMap();

        const processNode = (node: Partial<IKb3dObject>, parentNamePath: string) => {
            const token = Token.withKey((node as any)['$type']);
            const namePath =
                token !== Token.SceneNode && node.name
                    ? [parentNamePath, node.name].filter(Boolean).join('/')
                    : parentNamePath;

            let matchingNode: Kb3dObject | undefined;
            if (token.isLight()) {
                matchingNode = nMap.lights.get(namePath);
            } else if (token.isMaterial()) {
                matchingNode = nMap.materials.get(namePath);
            } else {
                matchingNode = nMap.meshes.get(namePath);
            }

            const p = {
                node,
                type: Token.withKey((node as any)['$type']),
                namePath,
                matchingNode,
                operation: matchingNode ? ImportOperation.replace : ImportOperation.add,
            } as IImportPreview;
            p.children = ['meshes', 'materials', 'lights', 'viewpoints'].flatMap(childProp => {
                const childArr = (node as any)[childProp] as Kb3dObject[];
                return childArr
                    ? childArr.map(c => {
                          const cNode = processNode(c, childProp === 'meshes' ? namePath : '');
                          cNode.parent = p;
                          return cNode;
                      })
                    : [];
            });
            return p;
        };

        const pathToImportParent = parent.getNamePath();
        (imported.scene as any)['$type'] = 'SceneNode';
        return processNode(imported.scene, pathToImportParent).children;
    }

    public import(previews: IImportPreview[], parentNode: SpaceNode | MaterialNode, root = true) {
        const nMap = this.getNodeNamePathMap();

        // Materials have to come first so we know how they reconnect to the meshes to them
        previews.sort((a, b) => {
            if (a.type.isMaterial() && !b.type.isMaterial()) {
                return -1;
            } else {
                return 0;
            }
        });

        for (const p of previews) {
            if (p.operation !== ImportOperation.ignore) {
                let thisNode: Kb3dObject;

                if (p.operation === ImportOperation.add || !p.matchingNode) {
                    const parentPath = p.namePath.substring(0, p.namePath.lastIndexOf('/'));

                    let parent: Kb3dObject | undefined;
                    if (!parentPath) {
                        parent = parentNode;
                    } else if (p.type.isLight()) {
                        parent = nMap.lights.get(parentPath);
                    } else if (p.type.isMaterial()) {
                        parent = nMap.materials.get(parentPath);
                    } else {
                        parent = nMap.meshes.get(parentPath);
                    }

                    const newNode = this.deserialize(p.node, { includeChildNodes: false });
                    if (
                        instanceOfAny<LightNode>(newNode, ...Token.GlobalLightTokens) &&
                        instanceOf<SceneNode>(parentNode, Token.SceneNode)
                    ) {
                        parentNode.lights.push(newNode);
                    } else if (parent?.id === parentNode.id) {
                        parentNode?.addChildren(newNode);
                    }
                    thisNode = newNode;
                } else {
                    // replace
                    const originalId = p.matchingNode.id;
                    const matchingMeshNode = isMeshNode(p.matchingNode) ? p.matchingNode : undefined;
                    const originalMaterialId = matchingMeshNode ? matchingMeshNode.materialId : undefined;
                    this.assign(p.matchingNode, p.node, { includeChildNodes: false });

                    p.matchingNode.id = originalId;
                    // If the new materialId from the import exists, that means the material was imported and added and can be used.
                    // If the new materialId from the import does not exist, use the old materialId because the material was updated but
                    // kept the original ID.
                    if (matchingMeshNode && originalMaterialId && matchingMeshNode.materialId) {
                        if (!this.getById(matchingMeshNode.materialId)) {
                            matchingMeshNode.materialId = originalMaterialId;
                        }
                    }
                    // Trigger change detection on this existing node
                    if (isTrackedClass(p.matchingNode)) {
                        p.matchingNode.isModified = true;
                    }
                    thisNode = p.matchingNode;
                }

                if (thisNode instanceof SpaceNode || thisNode instanceof MaterialNode) {
                    this.import(p.children, thisNode, false);
                } else if (p.children && p.children.length > 0) {
                    throw Error('Children on non-space node or non-material-node, how did this happen');
                }
            }
        }
    }

    protected initializeModel<T>(model: T, args: Partial<T>, modelDef: IKb3dModel<T>, options?: IDeserializeOptions) {
        this.setModelDefaultValues(model);
        this.assign(model, args, options);
    }

    protected setModelDefaultValues(o: any) {
        const values = (defaults as any)[o.$type];
        if (values) {
            for (const prop in values) {
                if (values[prop] && values[prop].$type) {
                    o[prop] = this.deserialize(values[prop]);
                } else if (isObject(values[prop])) {
                    o[prop] = { ...values[prop] }; //need to clone the object from default values otherwise the class and defaults will both be pointing to same instance!
                } else {
                    o[prop] = values[prop];
                }
            }
        }
    }

    public assign(model: any, args: any, options?: IDeserializeOptions) {
        options = {
            includeChildNodes: true,
            issueNewIds: false,
            copyingToDifferentNode: false,
            ...options,
        };

        if (args) {
            if (options.copyingToDifferentNode) {
                // If we are using assign to copy properties from one node to another, serialize the data
                if (args instanceof Kb3dObject) {
                    const defaultValues = { $type: args.$type };
                    this.setModelDefaultValues(defaultValues);
                    args = { ...defaultValues, ...this.serialize(args) };
                }
            }

            for (const prop in args) {
                // Never persist properties beginning with an underscore
                if (prop.substr(0, 1) == '_') continue;
                if (options.copyingToDifferentNode) {
                    if ((prop.isEqual('id') && model.id) || prop.isEqual('$type')) {
                        continue; // skip id's if copying to an existing node so the node retains its ID
                    }
                }
                if (options.issueNewIds && prop.isEqual('id') && args.$type) continue; //skip id's if options specify so it will use the new one it generates during construction

                const sourceProp = args[prop];

                if (typeof model === 'undefined') {
                    throw Error(); // this should never happen, this means a reference is being lost somewhere
                }

                if (prop === 'parent' || sourceProp === model[prop]) {
                    // Don't break parent relationships
                    continue; // Don't break parent relationships
                }

                if (!isObject(args[prop])) {
                    // For primitives, just copy the values over
                    model[prop] = args[prop];
                } else if (Array.isArray(sourceProp)) {
                    // make sure there is an array on target prop.  Recreate it if one is there already.
                    if (model[prop]) {
                        if (
                            options.includeChildNodes ||
                            !(
                                model[prop].length > 0 &&
                                model[prop][0].$type &&
                                Token.withKey(model[prop][0].$type).isNode()
                            )
                        ) {
                            model[prop].clear();
                        }
                    } else {
                        model[prop] = [];
                    }

                    for (const sourceItem of sourceProp) {
                        let targetItem: any;
                        if (sourceItem.$type) {
                            //it's one of our types so deserialize it
                            if (options.includeChildNodes || !Token.withKey(sourceItem.$type).isNode()) {
                                targetItem = this.deserialize(sourceItem, options);
                                model[prop].push(targetItem);
                            }
                        } else if (isObject(sourceItem)) {
                            targetItem = {};
                            model[prop].push(targetItem);
                            this.assign(targetItem, sourceItem, options);
                        } else {
                            //primitive type
                            model[prop].push(sourceItem);
                        }
                    }
                } else {
                    if (sourceProp.$type) {
                        //it's one of our types so deserialize it
                        if (options.includeChildNodes || !Token.withKey(sourceProp.$type).isNode()) {
                            model[prop] = this.deserialize(sourceProp, options);
                        }
                    } else if (isObject(sourceProp)) {
                        const targetItem: any = {};
                        model[prop] = targetItem;
                        this.assign(targetItem, sourceProp, options);
                    } else {
                        //primitive type
                        model[prop] = sourceProp;
                    }
                }
            }
        }
    }

    protected shallowEquals<A extends object, B extends object>(a: A, b: B) {
        if (!isObject(a)) {
            return (a as any) === (b as any);
        }

        for (const aProp in a) {
            if (aProp.substring(0, 1) !== '_') {
                if (!(aProp in b)) {
                    return false;
                }
                if (a[aProp] !== (b as any)[aProp]) {
                    return false;
                }
            }
        }
        for (const bProp in b) {
            if (bProp.substring(0, 1) !== '_') {
                if (!(bProp in a)) {
                    return false;
                }
                if (b[bProp] !== (a as any)[bProp]) {
                    return false;
                }
            }
        }

        return true;
    }
}
